
import React from "react";
import PageLayout from "@/components/Layout/PageLayout";
import { HeroBanner } from "@/components/ui/hero-banner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Calendar, 
  Users, 
  ClipboardList, 
  FileText, 
  Hospital 
} from "lucide-react";
import AccessDenied from "@/components/AccessDenied";
import { useUserSession } from '@/hooks/useUserSession';

const DoctorPage: React.FC = () => {

  const { user, isLoaded } = useUserSession();

  // if (!user || user.role !== 'doctor') return <AccessDenied />;
  
  return (<PageLayout>
      <HeroBanner
        title="Doctor Portal"
        description="Access patient records, manage appointments, and coordinate care efficiently"
        backgroundColor="bg-gradient-to-r from-blue-700 to-blue-900"
      />

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="patients">Patients</TabsTrigger>
            <TabsTrigger value="appointments">Appointments</TabsTrigger>
            <TabsTrigger value="records">Medical Records</TabsTrigger>
            <TabsTrigger value="hospitals">Hospitals</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Total Patients</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Users className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">248</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">+12% from last month</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Appointments Today</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">12</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">2 pending confirmations</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Medical Records</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">1,024</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">32 updated this week</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Associated Hospitals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Hospital className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">3</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Primary: General Hospital</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Upcoming Appointments</CardTitle>
                  <CardDescription>Your schedule for the next few days</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map((_, i) => (
                      <div key={i} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="mr-4 flex-shrink-0">
                          <div className="bg-blue-100 text-blue-600 rounded-full h-12 w-12 flex items-center justify-center">
                            {String.fromCharCode(65 + i)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            Patient Name {i + 1}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            General Checkup
                          </p>
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(Date.now() + i * 24 * 60 * 60 * 1000).toLocaleDateString()} - 10:0{i} AM
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activities</CardTitle>
                  <CardDescription>Your latest actions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      "Updated John Doe's medical records",
                      "Prescribed medication for Sarah Smith",
                      "Scheduled follow-up with Mike Johnson",
                      "Reviewed lab results for Emily Brown",
                      "Completed telemedicine session"
                    ].map((activity, i) => (
                      <div key={i} className="flex items-start">
                        <div className="mr-2 mt-0.5">
                          <ClipboardList className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm">{activity}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(Date.now() - i * 2 * 60 * 60 * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="patients">
            <Card>
              <CardHeader>
                <CardTitle>Your Patients</CardTitle>
                <CardDescription>View and manage your patient list</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Age</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Gender</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Condition</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Last Visit</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">Patient Name {i + 1}</td>
                          <td className="px-4 py-3 text-sm">{25 + i}</td>
                          <td className="px-4 py-3 text-sm">{i % 2 === 0 ? "Male" : "Female"}</td>
                          <td className="px-4 py-3 text-sm">
                            {["Hypertension", "Diabetes", "Asthma", "Arthritis", "Allergies"][i % 5]}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {new Date(Date.now() - i * 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex space-x-2">
                              <button className="text-blue-600 hover:text-blue-800 font-medium">View</button>
                              <button className="text-green-600 hover:text-green-800 font-medium">Update</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="appointments">
            <Card>
              <CardHeader>
                <CardTitle>Appointment Calendar</CardTitle>
                <CardDescription>Manage your schedule</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-7 gap-4 text-center">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                    <div key={day} className="font-medium">{day}</div>
                  ))}
                  {Array.from({ length: 28 }).map((_, i) => (
                    <div 
                      key={i} 
                      className={`p-2 border rounded-md ${i % 7 >= 5 ? 'bg-gray-100' : ''} ${[2, 5, 9, 14, 18, 21].includes(i) ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                    >
                      {i + 1}
                      {[2, 5, 9, 14, 18, 21].includes(i) && (
                        <div className="mt-1 text-xs font-medium text-blue-600">
                          {[2, 4, 3, 5, 2, 3][([2, 5, 9, 14, 18, 21].indexOf(i))] + " appts"}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="records">
            <Card>
              <CardHeader>
                <CardTitle>Medical Records</CardTitle>
                <CardDescription>Access and manage patient medical records</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <div className="flex justify-between">
                        <h3 className="font-medium">Patient {i + 1} - Medical Record</h3>
                        <span className="text-sm text-gray-500">
                          Updated: {new Date(Date.now() - i * 3 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-gray-600">
                        This record contains the complete medical history, diagnoses, treatments, medications, and follow-up plans.
                      </p>
                      <div className="mt-3 flex space-x-2">
                        <button className="text-blue-600 text-sm font-medium">View Full Record</button>
                        <button className="text-green-600 text-sm font-medium">Update</button>
                        <button className="text-purple-600 text-sm font-medium">Share</button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="hospitals">
            <Card>
              <CardHeader>
                <CardTitle>Associated Hospitals</CardTitle>
                <CardDescription>Manage your hospital affiliations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Card key={i} className="border shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle>General Hospital {i + 1}</CardTitle>
                        <CardDescription>
                          {["Main Campus", "East Wing", "West Wing"][i]}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 mb-4">
                          123 Medical Center Blvd, Suite {100 + i * 10}
                          <br />
                          Medical City, MC 12345
                        </p>
                        <div className="flex space-x-2">
                          <button className="text-blue-600 text-sm font-medium">View Details</button>
                          <button className="text-green-600 text-sm font-medium">Schedule</button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

export default DoctorPage;
